import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { trackEvent } from "@/hooks/useAnalytics";
import {
  Flame, ArrowRight, Check, X, Lock, ShieldCheck, Award, Bot, BookOpen, Cpu,
  Clock, Star, AlertTriangle, Zap, FileCheck2, GraduationCap, ChevronDown,
} from "lucide-react";

// Single-page conversion funnel — minimal nav, narrative arc, multiple CTAs.
// Lives at /start. Tracks events to power Google Analytics + future A/B testing.

const PRICE = 149;

const ENROLL_BTN_TEXT = "Start now — $149 lifetime →";

// ─── Section helpers ────────────────────────────────────────────────────
const Section = ({ id, dark, children, className = "" }) => (
  <section
    id={id}
    className={`px-6 sm:px-8 lg:px-12 py-16 sm:py-20 ${dark ? "bg-zinc-950 text-zinc-100" : "bg-white"} ${className}`}
  >
    <div className="max-w-5xl mx-auto">{children}</div>
  </section>
);

const Overline = ({ dark, children }) => (
  <div className={`overline mb-3 ${dark ? "text-zinc-500" : ""}`}><span className="red-bar" />{children}</div>
);

// ─── CTA Button ────────────────────────────────────────────────────────
const CtaButton = ({ location, large = false, secondary = false, children }) => (
  <Link
    to="/register"
    onClick={() => trackEvent("enroll_click", { location, funnel: "start" })}
    data-testid={`funnel-cta-${location}`}
  >
    <button
      className={`inline-flex items-center gap-2 font-semibold transition-all ${
        secondary
          ? "border-2 border-zinc-100 text-zinc-100 hover:bg-zinc-100 hover:text-zinc-950"
          : "bg-red-600 text-white hover:bg-red-500 shadow-[0_0_0_3px_rgba(220,38,38,0.15)] hover:shadow-[0_0_0_5px_rgba(220,38,38,0.25)]"
      } ${large ? "px-7 py-4 text-base sm:text-lg" : "px-5 py-3 text-sm"}`}
    >
      {children || ENROLL_BTN_TEXT}
      <ArrowRight className={large ? "w-5 h-5" : "w-4 h-4"} />
    </button>
  </Link>
);

// ─── Page ──────────────────────────────────────────────────────────────
export default function Funnel() {
  useDocumentMeta({
    title: "Pass NICET Level 1 in 8 weeks. $149 lifetime. — APM Fire Alarm",
    description: "The NICET prep course built for NICET candidates. 140 hrs of interactive curriculum, AI tutor, riser diagrams, panel simulator, NYC S-98 review, QR-verified certificate. One-time $149.",
    path: "/start",
  });

  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    trackEvent("funnel_view", { page: "start" });
  }, []);

  return (
    <div className="bg-white text-zinc-900" data-testid="funnel-page">
      {/* ── Minimal nav — logo only, no distractions ──────────────────── */}
      <header className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="funnel-logo">
            <div className="w-7 h-7 bg-red-600 grid place-items-center">
              <Flame className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-heading text-[14px] font-bold tracking-tight">APM Fire Alarm</div>
          </Link>
          <Link to="/login" className="text-xs font-mono uppercase tracking-wider text-zinc-600 hover:text-red-600" data-testid="funnel-login-link">
            Already enrolled? Log in
          </Link>
        </div>
      </header>

      {/* ═══ HERO ════════════════════════════════════════════════════════ */}
      <Section className="relative overflow-hidden">
        <div className="grid lg:grid-cols-12 gap-10 items-center relative z-10">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 mb-5" data-testid="funnel-eyebrow">
              <Flame className="w-3.5 h-3.5 text-red-600" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-red-600 font-semibold">Built for NICET candidates</span>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter leading-[0.95] mb-5" data-testid="funnel-headline">
              Pass NICET Level&nbsp;1<br />
              in <span className="text-red-600">8 weeks</span>.<br />
              For $149.
            </h1>

            <p className="text-lg sm:text-xl text-zinc-700 leading-relaxed max-w-xl mb-7" data-testid="funnel-subhead">
              The fire alarm prep course built for tradespeople. 140 hours of interactive curriculum,
              an AI tutor that answers every code question, a working panel simulator, and a
              QR-verified certificate when you pass — all for one flat fee.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-7">
              <CtaButton location="hero" large />
              <Link to="/sample" className="inline-flex items-center gap-2 px-5 py-4 border-2 border-zinc-300 hover:border-red-600 hover:text-red-600 transition-colors font-semibold text-sm" data-testid="funnel-sample-link" onClick={() => trackEvent("sample_click", { location: "hero" })}>
                <BookOpen className="w-4 h-4" /> Try a free lesson
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-mono text-zinc-500">
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-green-600" /> No subscription</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-green-600" /> Lifetime access</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-green-600" /> 7-day refund</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-green-600" /> Mobile-friendly</span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              {/* Stats card */}
              <div className="bg-zinc-950 text-zinc-100 p-7 border-4 border-red-600">
                <div className="overline text-zinc-500 mb-4"><span className="red-bar" />The numbers</div>
                <div className="grid grid-cols-2 gap-5">
                  <Stat n="140" l="hours of curriculum" />
                  <Stat n="15" l="modules · L1 · L2 · L3 · S-98" />
                  <Stat n="50" l="-question final exam" />
                  <Stat n="10" l="panel scenarios" />
                </div>
                <div className="mt-6 pt-5 border-t border-zinc-800">
                  <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Launch price</div>
                  <div className="font-heading text-5xl font-bold tracking-tighter text-red-500">${PRICE}</div>
                  <div className="text-xs text-zinc-500 mt-1">One-time. Pay once, study forever.</div>
                </div>
              </div>
              <div className="absolute -top-3 -right-3 bg-red-600 text-white px-3 py-1.5 font-mono text-xs uppercase tracking-widest font-bold transform rotate-3">
                Cheaper than 1 missed shift
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ═══ TRUST STRIP ═════════════════════════════════════════════════ */}
      <div className="border-y border-zinc-200 bg-zinc-50/50 py-6">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">Aligned with the standards your AHJ enforces</div>
          <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-zinc-700">
            {["NFPA 72", "NFPA 70 (NEC)", "UL 864", "UL 268", "NICET", "NYC FDNY S-98", "IBC §907"].map((s) => (
              <div key={s} className="font-mono text-sm font-semibold tracking-wide">{s}</div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ PROBLEM ══════════════════════════════════════════════════════ */}
      <Section dark>
        <Overline dark>The problem</Overline>
        <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tighter leading-tight mb-7 max-w-3xl">
          NICET trips up smart techs because <span className="text-red-500">prep is dry, expensive, and disconnected from the field.</span>
        </h2>
        <div className="grid sm:grid-cols-3 gap-5">
          {[
            { i: AlertTriangle, t: "$500-$1,200 in-person classes", d: "Industry seminars run 3 days, cost more than a week's pay, and end before you've absorbed the material." },
            { i: AlertTriangle, t: "PDFs that put you to sleep", d: "Most online prep is a 200-page PDF. No interaction, no practice, no way to ask 'wait — why?'" },
            { i: AlertTriangle, t: "Code memorization without context", d: "The exam tests how the code applies, not just what it says. Memorizing §10 alone won't get you to 70%." },
          ].map((p) => (
            <div key={p.t} className="border border-zinc-800 bg-zinc-900/40 p-5">
              <p.i className="w-6 h-6 text-red-500 mb-3" />
              <div className="font-heading text-lg font-bold tracking-tight mb-1">{p.t}</div>
              <p className="text-sm text-zinc-400 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-zinc-400 text-base sm:text-lg max-w-2xl leading-relaxed">
          Meanwhile NICET-certified techs earn{" "}
          <span className="text-zinc-100 font-semibold">15-25% more</span> than uncertified peers, and most union locals require Level 2+ for foreman scale.
        </p>
      </Section>

      {/* ═══ SOLUTION ════════════════════════════════════════════════════ */}
      <Section>
        <Overline>The solution</Overline>
        <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tighter leading-tight mb-7 max-w-3xl">
          A course that <span className="text-red-600">teaches you the field</span>, not just the test.
        </h2>

        <div className="grid sm:grid-cols-2 gap-5 mb-10">
          {[
            { i: BookOpen, t: "140 hours of written curriculum", d: "Across 15 modules: NICET Level 1, 2, 3 + NYC S-98. Code-by-code, with every NFPA 72, UL, and NEC reference cited inline." },
            { i: Bot, t: "AI Tutor — Claude Sonnet 4.5", d: "Stuck on a battery calc or pathway class? Ask the tutor. Unlimited questions. Fluent in NFPA 72, NEC 760, NFPA 92, UL 864." },
            { i: Cpu, t: "Working panel simulator", d: "Practice ACK / SILENCE / RESET / DRILL on a simulated FACU. 10 NICET-aligned scenarios. Drill the muscle memory." },
            { i: Award, t: "QR-verified certificate", d: "Pass the 50-question final exam with 70%+ and earn a printable PDF + public verification page. Send the link to employers." },
          ].map((f) => (
            <div key={f.t} className="card-tech p-6">
              <div className="w-10 h-10 bg-red-600 text-white grid place-items-center mb-3">
                <f.i className="w-5 h-5" />
              </div>
              <div className="font-heading text-xl font-bold tracking-tight mb-1.5">{f.t}</div>
              <p className="text-sm text-zinc-700 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <CtaButton location="solution" large />
        </div>
      </Section>

      {/* ═══ COMPARE ═════════════════════════════════════════════════════ */}
      <Section className="bg-zinc-50">
        <Overline>How it compares</Overline>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-8">
          We're not the cheapest option — but we're the only one with these.
        </h2>

        <div className="overflow-x-auto -mx-2 px-2 pb-2">
          <table className="w-full min-w-[640px] text-sm border-2 border-zinc-200 bg-white" data-testid="funnel-compare-table">
            <thead className="bg-zinc-950 text-zinc-100">
              <tr>
                <th className="text-left p-4 font-heading text-base">Feature</th>
                <th className="p-4 font-heading text-base text-red-500">APM ($149)</th>
                <th className="p-4 font-heading text-base text-zinc-400">In-person seminar</th>
                <th className="p-4 font-heading text-base text-zinc-400">Self-study book</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["140 hrs interactive curriculum", true, false, false],
                ["NICET L1 + L2 + L3 + NYC S-98", true, false, false],
                ["AI tutor (unlimited Q&A)", true, false, false],
                ["Working FACU panel simulator", true, false, false],
                ["Interactive riser diagrams", true, false, false],
                ["Lifetime access + future updates", true, false, true],
                ["QR-verified certificate", true, true, false],
                ["Mobile + tablet access", true, false, true],
                ["One-time payment under $200", true, false, true],
              ].map((row) => (
                <tr key={row[0]} className="border-t border-zinc-100">
                  <td className="p-4 text-zinc-800 font-medium">{row[0]}</td>
                  <td className="p-4 text-center">{row[1] ? <Check className="w-5 h-5 text-green-600 mx-auto" strokeWidth={2.5} /> : <X className="w-5 h-5 text-zinc-300 mx-auto" />}</td>
                  <td className="p-4 text-center">{row[2] ? <Check className="w-5 h-5 text-zinc-400 mx-auto" /> : <X className="w-5 h-5 text-zinc-300 mx-auto" />}</td>
                  <td className="p-4 text-center">{row[3] ? <Check className="w-5 h-5 text-zinc-400 mx-auto" /> : <X className="w-5 h-5 text-zinc-300 mx-auto" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* ═══ INSIDE THE COURSE ══════════════════════════════════════════ */}
      <Section>
        <Overline>What's inside</Overline>
        <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tighter leading-tight mb-3 max-w-3xl">
          15 modules. 140 hours. Every code reference your AHJ uses.
        </h2>
        <p className="text-zinc-700 max-w-2xl leading-relaxed mb-8">
          Designed to take you from "what's a SLC?" to "I just passed NICET Level 1" — and far enough to attempt Level 2 or NYC S-98 right after.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          {[
            { tag: "L1 · 70 hrs", t: "NICET Foundation + NYC S-98", body: ["M1 Intro to Fire Alarm Systems", "M2 Code Requirements (NFPA 72)", "M3 Power Supplies & Circuits", "M4 Testing, Inspection & Maintenance", "M5 Final Exam Prep", "M6 NYC S-98 Cert of Fitness Review"] },
            { tag: "L2 · 30 hrs", t: "Detection + Design + Comms", body: ["M7 Advanced Detection & Special Hazards", "M8 System Design + Battery Calcs", "M9 Off-Premises Comm + Supervising Stations", "M10 Mass Notification + ECS"] },
            { tag: "L3 · 40 hrs", t: "Engineering + Smoke Control", body: ["M11 Performance-Based Design", "M12 Supervising-Station Operations", "M13 Smoke Control & Management (NFPA 92)", "M14 Healthcare / Transit / ESS Specialty", "M15 NICET Path & Project Management"] },
            { tag: "Bonus", t: "Lab + Glossary + AI Tutor", body: ["4 interactive riser diagrams", "FACU panel simulator (10 scenarios)", "85-term searchable glossary", "Unlimited AI tutor (Claude 4.5)"] },
          ].map((b) => (
            <div key={b.t} className="card-tech p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-red-600 text-white px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest font-bold">{b.tag}</span>
                <h3 className="font-heading text-lg font-bold tracking-tight">{b.t}</h3>
              </div>
              <ul className="space-y-1.5 text-sm text-zinc-700">
                {b.body.map((x) => (
                  <li key={x} className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* ═══ TIMELINE ════════════════════════════════════════════════════ */}
      <Section className="bg-zinc-50">
        <Overline>Your 8-week path</Overline>
        <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tighter leading-tight mb-8 max-w-3xl">
          From signup to certified — week by week.
        </h2>

        <div className="space-y-4">
          {[
            { w: "Wk 1-2", t: "Foundations", d: "M1-M2: Initiating + notification devices, NFPA 72 fundamentals. ~20 hrs reading + 3 quizzes." },
            { w: "Wk 3-4", t: "Power & ITM", d: "M3-M4: Battery calcs, NEC 760 wiring, acceptance testing, ITM Table 14.4.3.2. Quizzes + practice problems." },
            { w: "Wk 5-6", t: "S-98 + Lab", d: "M6 NYC review + interactive riser diagrams + panel simulator scenarios. Build muscle memory for field response." },
            { w: "Wk 7", t: "Final exam prep", d: "M5 + 50-question practice exam (timed, 120 min). Drill missed questions until you're at 80%+." },
            { w: "Wk 8", t: "Certify + go for L2", d: "Earn QR-verified certificate, then either book the real NICET exam, or roll into M7-M15 for Level 2 / 3." },
          ].map((s, i) => (
            <div key={s.w} className="grid grid-cols-12 gap-4 items-start border-l-4 border-red-600 pl-5 py-2">
              <div className="col-span-3 sm:col-span-2">
                <div className="font-mono text-[11px] uppercase tracking-widest text-red-600 font-bold">{s.w}</div>
                <div className="text-xs text-zinc-500 font-mono">Step {i + 1}</div>
              </div>
              <div className="col-span-9 sm:col-span-10">
                <div className="font-heading text-lg font-bold tracking-tight">{s.t}</div>
                <p className="text-sm text-zinc-700 leading-relaxed">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ═══ MEET YOUR INSTRUCTOR ═══════════════════════════════════════ */}
      <Section className="bg-zinc-950 text-zinc-100">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-4">
            <div className="aspect-square bg-zinc-900 border-4 border-red-600 grid place-items-center max-w-xs">
              <div className="text-center px-6">
                <div className="w-20 h-20 rounded-full bg-red-600 grid place-items-center mx-auto mb-3">
                  <span className="font-heading text-3xl font-bold tracking-tighter text-white">TM</span>
                </div>
                <div className="font-heading text-xl font-bold tracking-tight text-white">Trevor Marson</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-1">Lead Instructor · IBEW Local 3</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-8">
            <Overline dark>Your instructor</Overline>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tighter leading-tight mb-4">
              15+ years on the wire.<br />
              <span className="text-red-500">From WTC to JFK.</span>
            </h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Trevor Marson has installed, inspected, and serviced fire-alarm systems in the most
              demanding buildings in NYC — World Trade Center, Hudson Yards, JPMorgan Chase, Federal Reserve
              Bank of NY, JFK International Airport, NYU, John Jay College, and 100+ FDNY-witnessed inspections.
              He holds FDNY <strong className="text-zinc-100"> S-95, S-98, S-12, S-13, and F-53</strong> certs plus OSHA 30.
              Every lesson in this course is grounded in what he's actually seen on the job.
            </p>
            <div className="flex flex-wrap gap-2 mb-5">
              {["FDNY S-95", "FDNY S-98", "FDNY S-12", "FDNY S-13", "FDNY F-53", "OSHA 30", "IBEW Local 3 H"].map((c) => (
                <span key={c} className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 bg-zinc-900 border border-zinc-800 text-zinc-300">{c}</span>
              ))}
            </div>
            <Link to="/about-instructor" onClick={() => trackEvent("instructor_bio_click", { location: "funnel" })} data-testid="funnel-meet-instructor">
              <button className="border-2 border-zinc-100 text-zinc-100 hover:bg-zinc-100 hover:text-zinc-950 px-5 py-3 font-semibold inline-flex items-center gap-2 text-sm transition-colors">
                Read Trevor's full bio <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </Section>

      {/* ═══ TESTIMONIALS (placeholders user can edit) ══════════════════ */}
      <Section>
        <Overline>What students say</Overline>
        <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tighter leading-tight mb-8">
          Real techs. Real raises.
        </h2>

        <div className="grid md:grid-cols-3 gap-5">
          {[
            { name: "Marcus T.", role: "Apprentice, Local 3 IBEW", q: "I'd been studying off PDFs for 2 months and still didn't feel ready. APM's panel simulator is the single thing that made it click. Passed Level 1 first try." },
            { name: "Priya S.", role: "Fire alarm tech, NYC", q: "I needed both NICET 1 AND the FDNY S-98. Found one course that did both — for less than I'd been quoted for an in-person S-98 class alone." },
            { name: "Devin R.", role: "2nd-year apprentice", q: "The AI tutor saved me. I'd ask it about ground-fault detection at 11pm before a shift and it gave me a clearer answer than my journeyman." },
          ].map((t) => (
            <div key={t.name} className="card-tech p-6 flex flex-col">
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => <Star key={`star-${i}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
              </div>
              <p className="text-sm text-zinc-800 leading-relaxed mb-5 flex-1">"{t.q}"</p>
              <div className="border-t border-zinc-100 pt-3">
                <div className="font-semibold text-sm">{t.name}</div>
                <div className="text-xs text-zinc-500">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-zinc-400 font-mono mt-4">* Representative student profiles — replace with real testimonials as your cohort grows.</p>
      </Section>

      {/* ═══ GUARANTEE ═══════════════════════════════════════════════════ */}
      <Section className="bg-zinc-50">
        <div className="grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-3 grid place-items-center">
            <div className="w-32 h-32 rounded-full bg-red-600 text-white grid place-items-center border-8 border-red-100">
              <ShieldCheck className="w-14 h-14" strokeWidth={1.8} />
            </div>
          </div>
          <div className="md:col-span-9">
            <Overline>7-day no-questions guarantee</Overline>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-3">
              Try it for a week. If you're not sold, email us — full refund.
            </h2>
            <p className="text-zinc-700 leading-relaxed max-w-xl">
              Open the course, try the panel simulator, ask the AI tutor a few NFPA questions, walk through M1.
              If it doesn't beat anything else you've used, reply to your receipt within 7 days and we'll refund you. No forms, no friction.
            </p>
          </div>
        </div>
      </Section>

      {/* ═══ PRICING ═════════════════════════════════════════════════════ */}
      <Section dark>
        <div className="text-center mb-8">
          <Overline dark>One price. One time.</Overline>
          <h2 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-tight">
            Pay $149. Study for life.
          </h2>
        </div>

        <div className="max-w-md mx-auto bg-white text-zinc-900 p-8 border-4 border-red-600 relative">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-600 text-white px-3 py-1 font-mono text-[10px] uppercase tracking-widest font-bold">
            Founding-cohort price
          </div>
          <div className="text-center mb-5">
            <div className="font-heading text-7xl font-bold tracking-tighter leading-none">${PRICE}</div>
            <div className="text-sm text-zinc-500 mt-1">one-time · no subscription</div>
          </div>
          <ul className="space-y-2.5 mb-6 text-sm">
            {[
              "Lifetime access (yours forever)",
              "All future module updates included",
              "140 hrs · 15 modules · L1·L2·L3·S-98",
              "Unlimited AI tutor + lab + glossary",
              "QR-verified certificate on pass",
              "7-day refund — no questions",
            ].map((x) => (
              <li key={x} className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                <span>{x}</span>
              </li>
            ))}
          </ul>
          <CtaButton location="pricing" large />
          <div className="flex items-center justify-center gap-2 text-xs text-zinc-500 mt-4 font-mono">
            <Lock className="w-3 h-3" /> Secure · Powered by Stripe
          </div>
        </div>
      </Section>

      {/* ═══ FAQ ═════════════════════════════════════════════════════════ */}
      <Section>
        <Overline>Common questions</Overline>
        <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tighter leading-tight mb-8">
          Anything you need to know before you click.
        </h2>

        <div className="space-y-2" data-testid="funnel-faq">
          {[
            { q: "Will this course let me sit for the actual NICET exam?", a: "Our course is exam preparation — it doesn't enroll you with NICET. You register separately at nicet.org. Our role is to get you to a 70%+ on the practice exam, which historically correlates with passing the real one." },
            { q: "Is this a recognized credential?", a: "The certificate you earn here is a course-completion credential issued by APM, with QR-verified authenticity. The NICET certification itself is issued by the National Institute for Certification in Engineering Technologies after you pass their proctored exam." },
            { q: "How long do I have to complete it?", a: "Forever. You buy once, you keep it. You can take 4 weeks or 4 years. You can come back later for Level 2 / 3 prep without paying again." },
            { q: "Do I need to study every module?", a: "No. If you only want NICET Level 1, you can stop at Module 5 — that's 50 hrs and the 50-question final exam. The Level 2/3 + S-98 modules are bonuses you can ignore (or hit later)." },
            { q: "What if I'm in NYC and need the FDNY S-98?", a: "Module 6 is a complete S-98 review — NYC Fire Code Ch. 9, 3 RCNY §4604, MEA listings, FDNY violations, plus a 20-question FDNY-style practice quiz. Many NYC techs use APM specifically for this." },
            { q: "I haven't started exam prep yet — can I still learn this?", a: "Yes. The first 10 hours of M1 assume you've never opened a code book. We build up from 'what's a smoke detector?' to 'design a Class A SLC pathway across a 5-story occupancy.'" },
            { q: "Do you offer payment plans?", a: "Not currently — at $149 one-time we keep the price under what most courses charge for a payment plan deposit. If $149 is genuinely a barrier, the 7-day refund guarantee means you can finish a chunk and decide to cancel risk-free." },
            { q: "Can I share my login with a coworker?", a: "Each enrollment is per-person. If you want to gift the course to a coworker or apprentice, use our Gift flow — buy a code, send them a redemption link, both accounts unlock independently." },
          ].map((f, i) => (
            <div key={f.q} className="border-2 border-zinc-200 hover:border-red-600 transition-colors">
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left"
                data-testid={`funnel-faq-${i}`}
              >
                <span className="font-heading text-base sm:text-lg font-bold tracking-tight">{f.q}</span>
                <ChevronDown className={`w-5 h-5 text-zinc-500 flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180 text-red-600" : ""}`} />
              </button>
              {openFaq === i && (
                <div className="px-5 pb-5 text-sm text-zinc-700 leading-relaxed" data-testid={`funnel-faq-answer-${i}`}>
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* ═══ FINAL CTA ═══════════════════════════════════════════════════ */}
      <Section className="bg-red-600 text-white">
        <div className="text-center max-w-3xl mx-auto">
          <GraduationCap className="w-12 h-12 mx-auto mb-5 opacity-90" strokeWidth={1.5} />
          <h2 className="font-heading text-4xl sm:text-6xl font-bold tracking-tighter leading-[0.95] mb-5">
            Stop reading PDFs.<br />
            Start passing.
          </h2>
          <p className="text-base sm:text-lg opacity-95 max-w-xl mx-auto mb-7 leading-relaxed">
            Every day you delay is another shift you're not getting paid your certified rate.
            Lock the price in, lock the certification in, and get back to work.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" data-testid="funnel-cta-final" onClick={() => trackEvent("enroll_click", { location: "final_cta", funnel: "start" })}>
              <button className="bg-zinc-950 hover:bg-zinc-900 text-white px-8 py-4 font-semibold inline-flex items-center gap-2 text-base sm:text-lg transition-colors">
                <FileCheck2 className="w-5 h-5" /> Enroll for $149 <ArrowRight className="w-5 h-5" />
              </button>
            </Link>
            <Link to="/sample" data-testid="funnel-final-sample" onClick={() => trackEvent("sample_click", { location: "final_cta" })}>
              <button className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 font-semibold inline-flex items-center gap-2 text-base sm:text-lg transition-colors">
                <BookOpen className="w-5 h-5" /> Try a free lesson first
              </button>
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs font-mono opacity-90">
            <span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3" /> Secure checkout</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> 7-day refund</span>
            <span className="inline-flex items-center gap-1.5"><Zap className="w-3 h-3" /> Instant access</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3 h-3" /> Lifetime updates</span>
          </div>
        </div>
      </Section>

      {/* ═══ FOOTER ══════════════════════════════════════════════════════ */}
      <footer className="bg-zinc-950 text-zinc-500 py-10 px-6 sm:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-mono">
          <div className="flex items-center gap-2">
            <Flame className="w-4 h-4 text-red-600" />
            <span>© APM Fire Alarm Exam Prep</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/" className="hover:text-zinc-100">Home</Link>
            <Link to="/sample" className="hover:text-zinc-100">Free lesson</Link>
            <Link to="/login" className="hover:text-zinc-100">Log in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

const Stat = ({ n, l }) => (
  <div>
    <div className="font-heading text-4xl font-bold tracking-tighter text-red-500 leading-none">{n}</div>
    <div className="text-xs font-mono text-zinc-500 uppercase tracking-wider mt-1">{l}</div>
  </div>
);
